import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import generatePaymentReceiptPDF from './PaymentReceiptGenerator';
import { formatAmount } from './numberFormatter';

Modal.setAppElement('#root');

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  if (!date) return '—';
  const d = date instanceof Date ? date : date?.toDate?.();
  if (!d) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const CheckoutEntries = () => {
  const { user, userData } = useUserAuth();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [checkoutEntries, setCheckoutEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [hotelAccounts, setHotelAccounts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [groupedEntries, setGroupedEntries] = useState([]);
  const [newBatchId, setNewBatchId] = useState('');

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [currentCheckoutEntry, setCurrentCheckoutEntry] = useState(null); // ← to know which checkout to update
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [entryToTransfer, setEntryToTransfer] = useState(null);
  const [transferBooking, setTransferBooking] = useState(null);
  const [newAccountId, setNewAccountId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [incidentalTotal, setIncidentalTotal] = useState(0);

  const [paymentAmount, setPaymentAmount] = useState('');
  const [payerName, setPayerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Transfer');
  const [paymentNotes, setPaymentNotes] = useState('');

  const [paymentToVoid, setPaymentToVoid] = useState(null);
  const [isVoidConfirmModalOpen, setIsVoidConfirmModalOpen] = useState(false);
  const [selectedPaymentAccountId, setSelectedPaymentAccountId] = useState('');

  useEffect(() => {
    if (!user || !userData) return;
    setLoading(false);
    fetchAccounts();
    fetchHotelAccounts();
    fetchBatches();
    fetchCheckoutEntries();
  }, [user, userData]);

  const fetchAccounts = async () => {
    try {
      const snap = await getDocs(collection(db, "accounts"));
      setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
    //   console.error("Accounts fetch failed:", err);
    }
  };

  const fetchHotelAccounts = async () => {
    try {
      const snap = await getDocs(collection(db, "hotelAccounts"));
      setHotelAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
    //   console.error("Accounts fetch failed:", err);
    }
  };

  const fetchBatches = async () => {
    try {
      const q = query(collection(db, "batches"), where("active", "==", true));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBatches(list.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    }
  };

  const fetchLatestBooking = async (bookingId) => {
    try {
      const docSnap = await getDoc(doc(db, "bookings", bookingId));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (err) {
      console.error("Failed to fetch latest booking:", err);
      return null;
    }
  };

  const fetchCheckoutEntries = async () => {
    setUpdating(true);
    try {
      const q = query(
        collection(db, "checkoutEntries"),
        where("type", "==", "checkout_balance_transfer")
      );
      const snap = await getDocs(q);
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      list.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() ?? 0;
        const tb = b.createdAt?.toMillis?.() ?? 0;
        return tb - ta;
      });

      setCheckoutEntries(list);
    } catch (err) {
      console.error("Checkout entries fetch failed:", err);
      setError("Could not load checkout transfers");
      setShowModal(true);
    } finally {
      setUpdating(false);
    }
  };

  const updateEmbeddedBookingInCheckout = async (checkoutId, updates) => {
    try {
      await updateDoc(doc(db, "checkoutEntries", checkoutId), {
        "booking.deposit": updates.deposit,
        "booking.balance": updates.balance,
        "booking.updatedAt": updates.updatedAt || Timestamp.now(),
      });
    } catch (err) {
      console.error("Failed to update embedded booking in checkout:", err);
      // non-blocking – don't interrupt user flow
    }
  };

  // Group entries by Batch + Search
  useEffect(() => {
    const term = searchTerm.toLowerCase().trim();

    // Filter first
    const filtered = checkoutEntries.filter(entry => {
        const desc      = (entry.description || '').toLowerCase();
        const guest     = (entry.booking?.guestName || '').toLowerCase();
        const room      = (entry.booking?.roomNumber || '').toLowerCase();
        const account   = (entry.lines?.[0]?.accountName || '').toLowerCase();
        const batchName = (entry.batchName || '').toLowerCase();
        const batchId   = (entry.batchId || '').toLowerCase();
        const jid       = (entry.checkoutId || '').toLowerCase();
        const dateStr   = dateFormatter.format(new Date(entry.date || Date.now())).toLowerCase();

        return (
        desc.includes(term) ||
        guest.includes(term) ||
        room.includes(term) ||
        account.includes(term) ||
        batchName.includes(term) ||
        batchId.includes(term) ||
        jid.includes(term) ||
        dateStr.includes(term)
        );
    });

    const grouped = {};

    filtered.forEach(entry => {
        let batchKey = "No Batch";

        // Priority 1: batchName directly on checkout entry
        if (entry.batchName && typeof entry.batchName === 'string' && entry.batchName.trim() !== '') {
            batchKey = entry.batchName.trim();
        }
        // Priority 2: batchName inside embedded booking
        else if (entry.booking?.batchName && typeof entry.booking.batchName === 'string' && entry.booking.batchName.trim() !== '') {
            batchKey = entry.booking.batchName.trim();
        }
        // Priority 3: batchId on entry → lookup from batches list
        else if (entry.batchId) {
            const found = batches.find(b => b.id === entry.batchId);
            if (found?.name) batchKey = found.name.trim();
        }
        // Priority 4: batchId inside booking → lookup
        else if (entry.booking?.batchId) {
            const found = batches.find(b => b.id === entry.booking.batchId);
            if (found?.name) batchKey = found.name.trim();
        }

        if (!grouped[batchKey]) grouped[batchKey] = [];
        grouped[batchKey].push(entry);
    });

    // Sort so "No Batch" is at the bottom
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
        if (a === "No Batch") return 1;
        if (b === "No Batch") return -1;
        return a.localeCompare(b);
    });

    const result = {};
    sortedKeys.forEach(key => result[key] = grouped[key]);

    setGroupedEntries(result);
    }, [searchTerm, checkoutEntries, batches]);

  const handleRecordPaymentClick = async (entry) => {
    setCurrentCheckoutEntry(entry);
    const freshBooking = await fetchLatestBooking(entry.bookingId);
    if (!freshBooking) {
      setError("Could not load latest booking data");
      setShowModal(true);
      return;
    }

    setSelectedBooking(freshBooking);
    setIncidentalTotal(freshBooking.incidentalTotal || 0);

    setPaymentAmount('');
    setPayerName(freshBooking.guestName || 'Company');
    setPaymentMethod('Transfer');
    setPaymentNotes(`Payment related to transfer ${freshBooking.guestName || ''} (${entry.checkoutId})`);
    setIsPaymentModalOpen(true);
  };

  const handleViewHistoryClick = async (entry) => {
    setCurrentCheckoutEntry(entry);
    const freshBooking = await fetchLatestBooking(entry.bookingId);
    if (!freshBooking) {
      setError("Could not load latest booking data");
      setShowModal(true);
      return;
    }

    setSelectedBooking(freshBooking);
    setIncidentalTotal(freshBooking.incidentalTotal || 0);

    await fetchPaymentHistory(freshBooking);
    setIsHistoryModalOpen(true);
  };

  const handleViewDetailsClick = (entry) => {
    setSelectedEntry(entry);
    setIsDetailModalOpen(true);
  };

  const handleTransferClick = async (entry) => {
    setCurrentCheckoutEntry(entry);
    const freshBooking = await fetchLatestBooking(entry.bookingId);
    setTransferBooking(freshBooking || entry.booking || {});
    setEntryToTransfer(entry);
    const currentAccId = entry.lines?.[0]?.account || '';
    setNewAccountId(currentAccId);
    setTransferNotes('');
    setIsTransferModalOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (!entryToTransfer || !newAccountId) {
        setError("Please select a target account");
        setShowModal(true);
        return;
    }

    // if (newAccountId === entryToTransfer.lines?.[0]?.account) {
    //     setError("Cannot transfer to the same account");
    //     setShowModal(true);
    //     return;
    // }

    setUpdating(true);
    try {
        const selectedAccount = accounts.find(acc => acc.id === newAccountId);
        if (!selectedAccount) throw new Error("Selected account not found");

        const now = Timestamp.now();
        const transferBy = userData
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Staff'
        : 'Staff';

        const updateData = {
        "lines.0.account": newAccountId,
        "lines.0.accountName": selectedAccount.name || selectedAccount.accountName || 'Unknown Account',
        lastTransferBy: transferBy,
        lastTransferAt: now,
        lastTransferNotes: transferNotes.trim() || null,
        };

        // Add batch update if user selected a different batch
        if (newBatchId && newBatchId !== "none") {
        const selectedBatch = batches.find(b => b.id === newBatchId);
        updateData.batchId = newBatchId;
        updateData.batchName = selectedBatch?.name || null;
        } else if (newBatchId === "none") {
        // Explicitly remove batch
        updateData.batchId = null;
        updateData.batchName = null;
        }

        await updateDoc(doc(db, "checkoutEntries", entryToTransfer.id), updateData);

        setSuccessMessage("Transfer completed successfully");
        setShowModal(true);

        // Reset states
        setIsTransferModalOpen(false);
        setEntryToTransfer(null);
        setTransferBooking(null);
        setNewAccountId('');
        setNewBatchId('');
        setTransferNotes('');

        // Refresh list
        fetchCheckoutEntries();
    } catch (err) {
        console.error("Transfer failed:", err);
        setError("Failed to transfer: " + (err.message || "Unknown error"));
        setShowModal(true);
    } finally {
        setUpdating(false);
    }
    };

  const handleRecordPayment = async () => {
    if (!selectedBooking?.id || !paymentAmount || Number(paymentAmount) <= 0) {
        setError("Please enter a valid payment amount");
        setShowModal(true);
        return;
    }

    if (!selectedPaymentAccountId) {
        setError("Please select a payment account");
        setShowModal(true);
        return;
    }

    const amount = Number(paymentAmount);
    const selectedAccount = hotelAccounts.find(a => a.id === selectedPaymentAccountId);

    if (!selectedAccount) {
        setError("Selected account not found");
        setShowModal(true);
        return;
    }

    setUpdating(true);

    try {
        const now = Timestamp.now();
        const cashierName = userData
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Staff'
        : 'Staff';

        // 1. Update booking deposit & balance
        const currentDeposit = selectedBooking.deposit || 0;
        const newDeposit = currentDeposit + amount;
        const newBalance = (selectedBooking.totalPrice || 0) + incidentalTotal - newDeposit;

        await updateDoc(doc(db, "bookings", selectedBooking.id), {
        deposit: newDeposit,
        balance: newBalance,
        updatedAt: now
        });

        // 2. Update embedded booking in checkout entry
        if (currentCheckoutEntry) {
        await updateEmbeddedBookingInCheckout(currentCheckoutEntry.id, {
            deposit: newDeposit,
            balance: newBalance,
            updatedAt: now
        });
        }        

        // 4. Activity log (optional but useful)
        await addDoc(collection(db, "bookings", selectedBooking.id, "activities"), {
        type: 'payment',
        amount,
        method: selectedAccount.name || 'Account Payment',
        payerName: payerName.trim() || selectedBooking.guestName || 'Company',
        notes: paymentNotes.trim(),
        cashierName,
        performedBy: cashierName,
        performedById: user?.uid || 'N/A',
        timestamp: now,
        paymentAccountId: selectedPaymentAccountId,
        paymentAccountName: selectedAccount.name
        });

        // 5. Create proper Journal Entry (this is the key part)
        const journalEntriesRef = collection(db, "journalEntries");
        // Robust room number extraction
        const roomNumber = 
        selectedBooking?.roomNumber || 
        currentCheckoutEntry?.booking?.roomNumber || 
        selectedBooking?.room?.roomNumber || 
        '—';

        const description = `Payment received - ${selectedBooking?.guestName || 'Guest'} (Room ${roomNumber})`;        

        const journalData = {
        date: new Date().toISOString().split('T')[0],
        description,
        lines: [
            {
            accountName: selectedAccount.name,        // Debit: Cash / Bank / POS account increases
            debit: amount,
            credit: 0
            },
            {
            accountName: "Accounts Receivable - Guests",   // Credit: Reduces guest receivable
            debit: 0,
            credit: amount
            }
        ],
        userId: user?.uid,
        userName: cashierName,
        bookingId: selectedBooking.id,
        bookingReference: selectedBooking.roomNumber || '',
        checkoutId: currentCheckoutEntry?.checkoutId || '',
        type: "checkout_payment",
        createdAt: Timestamp.now(),
        journalId: ""
        };

        const docRef = await addDoc(journalEntriesRef, journalData);

        // Auto-generate journalId (same as your JournalEntries component)
        const journalId = docRef.id.substring(0, 8);
        await updateDoc(docRef, { journalId });

        // 3. Record in payments collection
        await addDoc(collection(db, "payments"), {
            bookingId: selectedBooking.id,
            guestName: selectedBooking.guestName || 'Unknown',
            guestPhone: selectedBooking.guestPhone || '',
            roomNumber: selectedBooking.roomNumber || 'N/A',
            amount,
            payerName: payerName.trim() || selectedBooking.guestName || 'Company',
            paymentMethod: selectedAccount.name || 'Account Payment',
            notes: paymentNotes.trim(),
            cashierName,
            timestamp: now,
            createdAt: now,
            paymentAccountId: selectedPaymentAccountId,
            journalDocId: docRef.id,
            journalId: journalId
        });

        setSuccessMessage(`₦${amount.toLocaleString()} payment recorded successfully`);

        // Reset form
        setIsPaymentModalOpen(false);
        setPaymentAmount('');
        setPayerName('');
        setPaymentNotes('');
        setSelectedPaymentAccountId('');

        // Refresh history if open
        if (isHistoryModalOpen) {
        const updatedBooking = { ...selectedBooking, deposit: newDeposit };
        setSelectedBooking(updatedBooking);
        await fetchPaymentHistory(updatedBooking);
        }

        // Refresh list (your existing refresh logic)
        if (currentCheckoutEntry?.bookingId) {
        const fresh = await fetchLatestBooking(currentCheckoutEntry.bookingId);
        if (fresh) {
            setCheckoutEntries(prev =>
            prev.map(e =>
                e.bookingId === currentCheckoutEntry.bookingId
                ? {
                    ...e,
                    booking: {
                        ...e.booking,
                        deposit: fresh.deposit,
                        balance: fresh.balance || ((fresh.totalPrice || 0) + (fresh.incidentalTotal || 0) - (fresh.deposit || 0)),
                        updatedAt: fresh.updatedAt
                    }
                    }
                : e
            )
            );
        }
        }
        setShowModal(true);
    } catch (err) {
        setError("Failed to record payment and journal entry");
        setShowModal(true);
    } finally {
        setUpdating(false);
    }
  };

  const fetchPaymentHistory = async (booking) => {
    setFetchingHistory(true);
    setPaymentHistory([]);
    try {
      const q = query(
        collection(db, "payments"),
        where("bookingId", "==", booking.id)
      );
      const snap = await getDocs(q);
      let history = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate?.() || new Date()
      }));

      history.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setPaymentHistory(history);
    } catch (err) {
      console.error("Payment history fetch failed:", err);
      setError("Failed to load payment history");
      setShowModal(true);
    } finally {
      setFetchingHistory(false);
    }
  };

  const handleVoidPayment = async () => {
    if (!paymentToVoid || !selectedBooking?.id) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, "payments", paymentToVoid.id), {
        voided: true,
        voidedAt: Timestamp.now(),
        voidedBy: {
          name: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Staff',
          userId: user?.uid || 'N/A'
        }
      });

      const currentDeposit = selectedBooking.deposit || 0;
      const newDeposit = currentDeposit - paymentToVoid.amount;
      if (newDeposit < 0) throw new Error("Cannot void – deposit would be negative");

      const newBalance = (selectedBooking.totalPrice || 0) + incidentalTotal - newDeposit;

      await updateDoc(doc(db, "bookings", selectedBooking.id), {
        deposit: newDeposit,
        balance: newBalance,
        updatedAt: Timestamp.now()
      });

      // Update embedded booking in checkout
      if (currentCheckoutEntry) {
        await updateEmbeddedBookingInCheckout(currentCheckoutEntry.id, {
          deposit: newDeposit,
          balance: newBalance,
          updatedAt: Timestamp.now()
        });
      }

      if (paymentToVoid.journalDocId) {
        try {
            await deleteDoc(doc(db, "journalEntries", paymentToVoid.journalDocId));
            // console.log("Journal entry deleted successfully");
        } catch (delErr) {
            // console.warn("Failed to delete journal entry:", delErr.message);
        }
      } 
        // Fallback: try by journalId (short ID) if full ID not stored
      else if (paymentToVoid.journalId) {
        // You would need to query here, but it's less reliable — better to store journalDocId
        // console.warn("journalDocId not found, skipping journal deletion");
      }

      setSuccessMessage(`Payment of ₦${paymentToVoid.amount.toLocaleString()} voided`);
      setIsVoidConfirmModalOpen(false);
      setPaymentToVoid(null);

      if (isHistoryModalOpen) {
        const updatedBooking = { ...selectedBooking, deposit: newDeposit };
        setSelectedBooking(updatedBooking);
        await fetchPaymentHistory(updatedBooking);
      }

      // Refresh only the affected row — MERGE, don't replace entire booking
      if (currentCheckoutEntry?.bookingId) {
        const fresh = await fetchLatestBooking(currentCheckoutEntry.bookingId);
        if (fresh) {
          setCheckoutEntries(prev =>
            prev.map(e =>
              e.bookingId === currentCheckoutEntry.bookingId
                ? {
                    ...e,
                    booking: {
                      ...e.booking,              // ← keep original embedded fields (including roomNumber)
                      deposit: fresh.deposit,
                      balance: fresh.balance || ((fresh.totalPrice || 0) + (fresh.incidentalTotal || 0) - (fresh.deposit || 0)),
                      updatedAt: fresh.updatedAt
                    }
                  }
                : e
            )
          );
          
          setFilteredEntries(prev =>
            prev.map(e =>
              e.bookingId === currentCheckoutEntry.bookingId
                ? {
                    ...e,
                    booking: {
                      ...e.booking,
                      deposit: fresh.deposit,
                      balance: fresh.balance || ((fresh.totalPrice || 0) + (fresh.incidentalTotal || 0) - (fresh.deposit || 0)),
                      updatedAt: fresh.updatedAt
                    }
                  }
                : e
            )
          );
        }
      }
      setShowModal(true);
    } catch (err) {
      setError(err.message || "Failed to void payment");
      setShowModal(true);
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !userData) return <Loader text={loading ? "LOADING" : "READING"} />;

  const allowed = ['Accountant', 'Admin', 'Supreme'].includes(userData.status);

  return (
    <>
      {updating && <Loader text="PROCESSING" />}

      {allowed ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">
              Checkout Entries
            </h2>
            <div onClick={fetchCheckoutEntries} style={{ cursor: 'pointer' }}>
              <i className="fa-solid fa-rotate-right size4" />
            </div>
          </div>
        
          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">
            <input
              type="text"
              className="input-field1"
              style={{ marginTop: '20px' }}
              placeholder="Search guest, room, account, batch, checkout ID, date..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {Object.keys(groupedEntries).length === 0 ? (
                  <p className="loading-text">
                    {searchTerm ? "No matching records found" : "No checkout transfers recorded yet"}
                  </p>
                ) : (
                  Object.entries(groupedEntries).map(([batchName, entries]) => (
                    <div key={batchName} style={{ marginBottom: '30px' }}>
                      {/* Batch Sub-heading */}                      
                      <h3 className="size4 bold font-gold" style={{ margin: 0}}>
                        {batchName} <small className="font-gold" style={{fontSize: '1rem'}}> ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})</small>
                      </h3>

                      {/* Entries under this batch */}
                      {entries.map((entry) => {
                        const booking = entry.booking || {};
                        const balance = booking.balance ?? 
                          ((booking.totalPrice || 0) + (booking.incidentalTotal || 0) - (booking.deposit || 0));

                        return (
                          <div
                            key={entry.id}
                            className="class-item class-item-products column"
                            style={{ 
                              padding: '16px 12px', 
                              marginBottom: '12px',
                              background: '#1e1e1e',
                            }}
                          >
                            <div className="column gap-1">
                              <div>
                                <strong>{booking.guestName || '—'}</strong><br />
                                <span><strong>Room:</strong> {booking.roomNumber || '—'}</span><br />
                                <span><strong>Account:</strong> {entry.lines?.[0]?.accountName || "—"}</span><br />
                                <span><strong>Paid:</strong> {formatAmount(booking.deposit || 0)}</span><br />
                                <span><strong>Balance:</strong> {formatAmount(balance)}</span><br />
                                <span><strong>ID:</strong> {entry.checkoutId || '—'}</span><br />
                                <span className="size5 font-gold bold">
                                  {dateFormatter.format(new Date(entry.date || Date.now()))}
                                </span>
                              </div>
                            </div>

                            <div className="align-center justify-center gap-15" style={{ marginTop: '16px' }}>
                              <button className="list-btn" onClick={() => handleRecordPaymentClick(entry)} title="Record payment">
                                <i className="fas fa-money-bill"></i>
                              </button>
                              <button className="list-btn" onClick={() => handleViewHistoryClick(entry)} title="Payment history">
                                <i className="fas fa-history"></i>
                              </button>
                              <button className="list-btn" onClick={() => handleViewDetailsClick(entry)} title="View details">
                                <i className="fas fa-eye"></i>
                              </button>
                              <button className="list-btn warning" onClick={() => handleTransferClick(entry)} title="Transfer account">
                                <i className="fas fa-exchange-alt"></i>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

        </>
      ) : (
        <p className="male-female-stat align-center justify-center">
          You don't have permission to view this page.
        </p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Payment Modal */}
      <Modal
        isOpen={isPaymentModalOpen}
        onRequestClose={() => setIsPaymentModalOpen(false)}
        className="custom-modal modal-scroll"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Record Payment</h2>
        </div>
        <div className="modal-body">
          {selectedBooking && (
            <>
              <p>
                <strong>Guest:</strong> {selectedBooking.guestName || '—'}<br />
                <strong>Room:</strong> {selectedBooking.roomNumber || currentCheckoutEntry?.booking?.roomNumber || '—'} ({selectedBooking.roomTypeName || currentCheckoutEntry?.booking?.roomTypeName || '—'})<br /><br />

                <strong>Room Charges:</strong> ₦{(selectedBooking.totalPrice || 0).toLocaleString()}<br />
                {incidentalTotal > 0 && (
                  <>
                    <strong>Incidental Charges:</strong> ₦{incidentalTotal.toLocaleString()}<br />
                  </>
                )}
                <br />
                <strong style={{ fontSize: '1.1em' }}>Total Due:</strong> ₦{((selectedBooking.totalPrice || 0) + incidentalTotal).toLocaleString()}<br />
                <strong>Paid So Far:</strong> ₦{(selectedBooking.deposit || 0).toLocaleString()}<br />
                <strong className="bold font-gold">Balance Due:</strong> ₦{(((selectedBooking.totalPrice || 0) + incidentalTotal) - (selectedBooking.deposit || 0)).toLocaleString()}
              </p>

              <div className="desktop-form-input">
                <div className="column">
                  <label className="input-label">Amount Paid (₦)</label>
                  <input
                    type="number"
                    className="input-field"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="column">
                  <label className="input-label">Payer Name</label>
                  <input
                    type="text"
                    className="input-field"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    placeholder="e.g. John Doe"
                  />
                </div>
              </div>    

              <div className="form-group column">
                <label className="input-label">Payment Account</label>
                <select
                    className="input-field"
                    value={selectedPaymentAccountId}
                    onChange={(e) => setSelectedPaymentAccountId(e.target.value)}
                    required
                >
                    <option value="">Select Account</option>
                    {hotelAccounts
                    .filter(acc => acc.active !== false)
                    .map(acc => (
                        <option key={acc.id} value={acc.id}>
                        {acc.name || acc.accountName || acc.id}
                        </option>
                    ))}
                </select>
              </div>




              <div className="form-group column">
                <label className="input-label">Notes (Optional)</label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g. Paid via bank transfer"
                />
              </div>

              <div className="row align-center justify-center gap-1 margin-top-20">
                <button className="login-button" onClick={() => setIsPaymentModalOpen(false)}>
                  Cancel
                </button>
                <button className="login-button" onClick={handleRecordPayment}>
                  Record Payment
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Payment History Modal */}
      <Modal
        isOpen={isHistoryModalOpen}
        onRequestClose={() => setIsHistoryModalOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Payment History</h2>
        </div>
        <div className="modal-body">
          {selectedBooking && (
            <>
              <p className="margin-bottom-15">
                <strong>Guest:</strong> {selectedBooking.guestName || '—'}<br />
                <strong>Room:</strong> {selectedBooking.roomNumber || currentCheckoutEntry?.booking?.roomNumber || '—' }
              </p>

              <div className="scroll-fixed extra-amenities history-display">
                {fetchingHistory ? (
                  <p className="text-center padding-20">Loading...</p>
                ) : paymentHistory.length === 0 ? (
                  <p className="text-center padding-20 color-gray">
                    No payments recorded yet
                  </p>
                ) : (
                  paymentHistory.map((payment) => (
                    <div
                      key={payment.id}
                      className="info-item"
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #333',
                        marginBottom: '10px',
                        background: payment.voided ? '#2a1f1f' : '#222',
                        borderRadius: '6px',
                        opacity: payment.voided ? 0.7 : 1,
                      }}
                    >
                      <div style={{ opacity: payment.voided ? 0.6 : 1 }}>
                        <strong>₦{payment.amount?.toLocaleString() || '0'}</strong><br />
                        Method: <strong>{payment.paymentMethod || '—'}</strong><br />
                        Payer: {payment.payerName || '—'}<br />
                        Cashier: <strong>{payment.cashierName || '—'}</strong><br />
                        {payment.notes && <>Notes: {payment.notes}</>}
                      </div>

                      <div className="align-center space-between">
                        <div className="column">
                          <span className="size5 bold font-gold">
                            {formatDateTime(payment.timestamp)}
                          </span>
                          {payment.voided && (
                            <span className="font-red bold size5">VOIDED</span>
                          )}
                        </div>

                        <div className="row gap-15">
                          {!payment.voided && (
                            <button
                              onClick={() => {
                                // 1. Start with whatever selectedBooking we have (from fetchLatestBooking)
                                let source = { ...selectedBooking };

                                // 2. Overlay with whatever is in the checkout entry's embedded booking
                                if (currentCheckoutEntry?.booking) {
                                  source = {
                                    ...source,
                                    ...currentCheckoutEntry.booking
                                  };
                                }

                                // 3. Create the safest possible object for the PDF generator
                                const safeBookingForPDF = {
                                  // Critical fields with fallbacks
                                  roomNumber:
                                    source?.roomNumber ||
                                    source?.room?.roomNumber ||
                                    currentCheckoutEntry?.booking?.roomNumber ||
                                    selectedBooking?.roomNumber ||
                                    "—",

                                  roomTypeName:
                                    source?.roomTypeName ||
                                    currentCheckoutEntry?.booking?.roomTypeName ||
                                    selectedBooking?.roomTypeName ||
                                    "—",

                                  guestName:
                                    source?.guestName ||
                                    currentCheckoutEntry?.booking?.guestName ||
                                    "—",

                                  totalPrice: Number(source?.totalPrice || 0),
                                  deposit: Number(source?.deposit || 0),
                                  nightlyRate: Number(source?.nightlyRate || 0),
                                  nights: Number(source?.nights || 0),

                                  // Dates — try every possible location and convert if needed
                                  checkIn:
                                    source?.checkIn ||
                                    source?.checkInDate ||
                                    currentCheckoutEntry?.booking?.checkIn ||
                                    currentCheckoutEntry?.booking?.checkInDate ||
                                    null,

                                  checkOut:
                                    source?.checkOut ||
                                    source?.checkOutDate ||
                                    currentCheckoutEntry?.booking?.checkOut ||
                                    currentCheckoutEntry?.booking?.checkOutDate ||
                                    null,

                                  // Also provide the names your PDF might be looking for
                                  checkInDate:
                                    source?.checkIn?.toDate?.() ||
                                    source?.checkInDate ||
                                    currentCheckoutEntry?.booking?.checkIn?.toDate?.() ||
                                    currentCheckoutEntry?.booking?.checkInDate ||
                                    null,

                                  checkOutDate:
                                    source?.checkOut?.toDate?.() ||
                                    source?.checkOutDate ||
                                    currentCheckoutEntry?.booking?.checkOut?.toDate?.() ||
                                    currentCheckoutEntry?.booking?.checkOutDate ||
                                    null,

                                  // Bonus fields that might help
                                  guestPhone: source?.guestPhone || currentCheckoutEntry?.booking?.guestPhone || "—",
                                  guestEmail: source?.guestEmail || currentCheckoutEntry?.booking?.guestEmail || "—",
                                };

                                // Optional: Log what we're actually sending (for debugging)
                                console.log("Data sent to receipt PDF:", {
                                  roomNumber: safeBookingForPDF.roomNumber,
                                  checkIn: safeBookingForPDF.checkIn,
                                  checkOut: safeBookingForPDF.checkOut,
                                  checkInDate: safeBookingForPDF.checkInDate,
                                  checkOutDate: safeBookingForPDF.checkOutDate,
                                  sourceKeys: Object.keys(source),
                                });

                                generatePaymentReceiptPDF(
                                  payment,
                                  safeBookingForPDF,
                                  payment.cashierName || 'Staff',
                                  incidentalTotal
                                );
                              }}
                              className="list-btn"
                              title="Print Receipt"
                              disabled={fetchingHistory}
                            >
                              <i className="fas fa-print"></i>
                            </button>
                          )}

                          {!payment.voided && (
                            <button
                              onClick={() => {
                                setPaymentToVoid(payment);
                                setIsVoidConfirmModalOpen(true);
                              }}
                              className="list-btn delete-btn"
                              title="Void Payment"
                            >
                              <i className="fas fa-trash-can"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="row align-center justify-center margin-top-25">
                <button className="login-button" onClick={() => setIsHistoryModalOpen(false)}>
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onRequestClose={() => {
          setIsDetailModalOpen(false);
          setSelectedEntry(null);
        }}
        className="custom-modal modal-scroll"
        overlayClassName="custom-modal-overlay"
      >
        {selectedEntry && (
          <>
            <div className="modal-header">
              <h2 className="size5 caps bold">Checkout Transfer Details</h2>
            </div>
            <div className="modal-body">
              <div>
                <h3 className="font-gold">Guest & Booking</h3>
                <p>
                  <span><strong>Guest:</strong> {selectedEntry.booking?.guestName || '—'}</span><br />
                  <span><strong>Phone:</strong> {selectedEntry.booking?.guestPhone || '—'}</span><br />
                  <span><strong>Email:</strong> {selectedEntry.booking?.guestEmail || '—'}</span><br />
                  <span><strong>Room:</strong> {selectedEntry.booking?.roomNumber || '—'} • {selectedEntry.booking?.roomTypeName || '—'}</span>
                </p>
              </div>

              <div>
                <h3 className="font-gold">Stay Period</h3>
                <p>
                  <span><strong>Booked Check-in:</strong> {formatDateTime(selectedEntry.booking?.checkIn)}</span><br />
                  <span><strong>Booked Check-out:</strong> {formatDateTime(selectedEntry.booking?.checkOut)}</span><br />
                  <span><strong>Actual Check-in:</strong> {formatDateTime(selectedEntry.booking?.checkInActual)}</span><br />
                  <span><strong>Actual Check-out:</strong> {formatDateTime(selectedEntry.booking?.checkOutActual)}</span><br />
                  <span><strong>Nights:</strong> {selectedEntry.booking?.nights || '—'}</span>
                </p>
              </div>

              <div>
                <h3 className="font-gold">Financial Summary</h3>
                <p>
                  <span>Nightly rate: <strong>{formatAmount(selectedEntry.booking?.nightlyRate || 0)}</strong></span><br />
                  <span>Room charges: <strong>{formatAmount(selectedEntry.booking?.totalPrice || 0)}</strong></span><br />
                  <span>Incidentals: <strong>{formatAmount(selectedEntry.booking?.incidentalTotal || 0)}</strong></span><br />
                  <span>Paid / Deposit (current): <strong>{formatAmount(selectedEntry.booking?.deposit || 0)}</strong></span><br />
                  <span className="font-gold bold">
                    Balance: {formatAmount(
                      (selectedEntry.booking?.totalPrice || 0) +
                      (selectedEntry.booking?.incidentalTotal || 0) -
                      (selectedEntry.booking?.deposit || 0)
                    )}
                  </span>
                </p>
              </div>

              <div>
                <h3 className="font-gold">Checkout Entry</h3>
                <p>
                  <span><strong>Checkout ID:</strong> {selectedEntry.checkoutId || '—'}</span><br />
                  <span><strong>Date:</strong> {dateFormatter.format(new Date(selectedEntry.date || Date.now()))}</span><br />
                  <span><strong>Created by:</strong> {selectedEntry.userName || 'System'}</span><br />
                  <span><strong>Description:</strong> {selectedEntry.description || "Balance transfer at checkout"}</span><br />
                  <span><strong>Target Account:</strong> {selectedEntry.lines?.[0]?.accountName || "—"}</span><br />
                  <span><strong>Batch:</strong> {selectedEntry.booking?.batchName || 'No Batch'}</span>
                </p>
              </div>
            </div>

            <div style={{ textAlign: 'center', margin: '24px 0 16px' }}>
              <button className="login-button" onClick={() => setIsDetailModalOpen(false)}>
                Close
              </button>
            </div>
          </>
        )}
      </Modal>
      
      {/* Transfer Account Modal */}
      <Modal
        isOpen={isTransferModalOpen}
        onRequestClose={() => {
            setIsTransferModalOpen(false);
            setEntryToTransfer(null);
            setTransferBooking(null);
            setNewAccountId('');
            setNewBatchId('');        // ← Reset new batch
            setTransferNotes('');
        }}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
        >
        {entryToTransfer && (
            <>
            <div className="modal-header">
                <h2 className="size5 caps bold">Transfer Checkout Entry</h2>
            </div>

            <div className="modal-body">
                <p style={{ marginBottom: '20px' }}>
                <strong>Guest:</strong> {entryToTransfer.booking?.guestName || '—'}<br />
                <strong>Room:</strong> {transferBooking?.roomNumber || entryToTransfer.booking?.roomNumber || '—'}<br />
                <strong>Checkout ID:</strong> {entryToTransfer.checkoutId || '—'}<br />
                <strong>Current Account:</strong>{' '}
                <span className="font-gold">
                    {entryToTransfer.lines?.[0]?.accountName || '—'}
                </span><br />
                <strong>Current Batch:</strong>{' '}
                <span className="font-gold">
                    {entryToTransfer.batchName || 
                    entryToTransfer.booking?.batchName || 
                    (entryToTransfer.batchId && batches.find(b => b.id === entryToTransfer.batchId)?.name) || 
                    'No Batch'}
                </span><br /><br />
                <strong>Paid So Far:</strong> {formatAmount(transferBooking?.deposit || entryToTransfer.booking?.deposit || 0)}<br />
                <strong>Balance:</strong>{' '}
                {formatAmount(
                    (transferBooking?.totalPrice || entryToTransfer.booking?.totalPrice || 0) +
                    (transferBooking?.incidentalTotal || entryToTransfer.booking?.incidentalTotal || 0) -
                    (transferBooking?.deposit || entryToTransfer.booking?.deposit || 0)
                )}
                </p>

                {/* Transfer to Account */}
                <div className="form-group column">
                <label className="input-label">Transfer to Account</label>
                <select
                    className="input-field"
                    value={newAccountId}
                    onChange={(e) => setNewAccountId(e.target.value)}
                >
                    <option value="">Select target account</option>
                    {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                        {acc.name || acc.accountName || acc.id}
                    </option>
                    ))}
                </select>
                </div>

                {/* New: Transfer to Batch */}
                <div className="form-group column" style={{ marginTop: '16px' }}>
                <label className="input-label">Transfer to Batch (Optional)</label>
                <select
                    className="input-field"
                    value={newBatchId || ''}
                    onChange={(e) => setNewBatchId(e.target.value)}
                >
                    <option value="">Keep current batch (No change)</option>
                    <option value="none">No Batch</option>
                    {batches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                        {batch.name}
                    </option>
                    ))}
                </select>                
                </div>

                <div className="form-group column" style={{ marginTop: '16px' }}>
                <label className="input-label">Notes / Reason (optional)</label>
                <textarea
                    className="input-field"
                    rows="2"
                    value={transferNotes}
                    onChange={(e) => setTransferNotes(e.target.value)}
                    placeholder="e.g. Guest requested change to company account and batch"
                />
                </div>

                <div className="row align-center justify-center gap-1 margin-top-30">
                <button
                    className="login-button"
                    onClick={() => setIsTransferModalOpen(false)}
                >
                    Cancel
                </button>
                <button
                    className="login-button warning-btn"
                    onClick={handleConfirmTransfer}
                    disabled={updating || !newAccountId}
                >
                    Confirm Transfer
                </button>
                </div>
            </div>
            </>
        )}
        </Modal>

      {/* Void Confirmation Modal */}
      <Modal
        isOpen={isVoidConfirmModalOpen}
        onRequestClose={() => setIsVoidConfirmModalOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Confirm Void Payment</h2>
        </div>
        <div className="modal-body">
          {paymentToVoid && (
            <>
              <p style={{ textAlign: 'center', marginBottom: '20px' }}>
                Void <strong>₦{paymentToVoid.amount?.toLocaleString() || '0'}</strong> via {paymentToVoid.paymentMethod || '—'}?<br />
                This action cannot be undone.
              </p>
              <div className="row align-center justify-center gap-1 margin-top-20">
                <button className="login-button" onClick={() => setIsVoidConfirmModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="login-button delete-btn"
                  onClick={handleVoidPayment}
                  disabled={updating}
                >
                  Void Payment
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default CheckoutEntries;